// src/components/layout/TraceabilityPageConfig.js

/**
 * Página de trazabilidad de combustible y distancias entre sitios.
 *
 * Responsabilidades:
 * - Consumir los datos de vehículos desde `useVehiculos(/api/comparativo/)`.
 * - Construir la estructura Marca → Modelo(Año) → Catálogos
 *   para alimentar las cards de consumo.
 * - Gestionar el estado del consumo por marca/modelo/catálogo
 *   conectado a la API de combustibles.
 * - Mostrar un resumen en un modal.
 * - Incluir la sección de distancias entre sitios (`SiteDistancesSection`).
 *
 * Estado principal:
 * - openBrandId / openModelBrandId: controlan qué combos están abiertos
 *   por marca (familia/año y catálogo).
 * - consumos: manejados por useCombustibles hook con integración API.
 * - openConsumoMarca / resumenConsumo: controlan la apertura y contenido
 *   del modal de resumen de consumos.
 */

import React, { useCallback, useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import TraceabilityMainConfig from './TraceabilityMainConfig';
import ConsumoCard from '../../cards/consumoCard';
import ConsumoResumenModal from '../../popups/ConsumoResumenModal';
import SiteDistancesSection from '../../SiteDistancesSection';
import { useVehiculos } from '../../../services/apiService';
import { NotificationContainer, ConfirmDialog } from '../../NotificationSystem';
import { useNotifications } from '../../../hooks/useNotifications';
import useCombustibles from '../../../hooks/useCombustibles'; // 🆕 Nuevo hook
//src\hooks\useCombustibles.js
//src\components\layout\TraceabilityPageConfig.js
/**
 * Helper que normaliza y agrupa los vehículos en una estructura navegable:
 *   Marca → [Modelos(Año)] → [Catálogos].
 *
 * Entrada:
 * - vehiculos: arreglo de objetos con al menos:
 *   { Marca, Familia, AnoModelo, Catalogo }.
 *
 * Salida:
 * - Arreglo de marcas ordenadas alfabéticamente, donde cada marca contiene:
 *   - id, marca
 *   - modelos: [{ id, familia, anoModelo, label, catalogos: [...] }]
 *
 * Uso:
 * - Sirve como fuente de datos para las `ConsumoCard`, evitando
 *   repetir lógica de agrupamiento y ordenamiento en el componente.
 */

const buildBrandModelCatalogs = (vehiculos = []) => {
  const brandMap = new Map();

  vehiculos.forEach((v) => {
    const { Marca, Familia, AnoModelo, Catalogo } = v;

    if (!Marca || !Familia || !AnoModelo || !Catalogo) return;

    const marcaKey = String(Marca).toUpperCase();

    if (!brandMap.has(marcaKey)) {
      brandMap.set(marcaKey, {
        id: marcaKey.toLowerCase().replace(/\s+/g, '-'),
        marca: Marca,
        modelosMap: new Map(),
      });
    }

    const marcaEntry = brandMap.get(marcaKey);
    const modelKey = `${Familia} ${AnoModelo}`;

    if (!marcaEntry.modelosMap.has(modelKey)) {
      marcaEntry.modelosMap.set(modelKey, {
        id: `${marcaKey}-${Familia}-${AnoModelo}`.replace(/\s+/g, '_'),
        familia: Familia,
        anoModelo: AnoModelo,
        label: `${Familia} ${AnoModelo}`,
        catalogosMap: new Map(),
      });
    }

    const modelEntry = marcaEntry.modelosMap.get(modelKey);

    if (!modelEntry.catalogosMap.has(Catalogo)) {
      modelEntry.catalogosMap.set(Catalogo, {
        code: Catalogo,
        label: `${Catalogo} - ${Familia}`,
      });
    }
  });

  const result = Array.from(brandMap.values()).map((marcaEntry) => {
    const modelos = Array.from(marcaEntry.modelosMap.values())
      .map((m) => ({
        id: m.id,
        familia: m.familia,
        anoModelo: m.anoModelo,
        label: m.label,
        catalogos: Array.from(m.catalogosMap.values()),
      }))
      .sort((a, b) => {
        if (a.familia === b.familia) {
          const ay = parseInt(a.anoModelo, 10) || 0;
          const by = parseInt(b.anoModelo, 10) || 0;
          return by - ay;
        }
        return a.familia.localeCompare(b.familia);
      });

    return {
      id: marcaEntry.id,
      marca: marcaEntry.marca,
      modelos,
    };
  });

  return result.sort((a, b) => a.marca.localeCompare(b.marca));
};

const TraceabilityPageConfig = () => {

  const [openBrandId, setOpenBrandId] = useState(null);
  const [openModelBrandId, setOpenModelBrandId] = useState(null);
  const [openConsumoMarca, setOpenConsumoMarca] = useState(null);
  const [resumenConsumo, setResumenConsumo] = useState({
    marca: null,
    rows: [],
  });
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const { notifications, addNotification, removeNotification } = useNotifications();
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false });
  const [resetTrigger, setResetTrigger] = useState(0);

  // Hook de combustibles con integración a API
  const {
    consumos,
    consumosByMarca,
    loading: combustiblesLoading,
    error: combustiblesError,
    guardarConsumo: guardarConsumoAPI,
    buscarConsumo,
    marcaTieneConsumos,
    isSubmitting,
    refrescarConsumos,
  } = useCombustibles(addNotification, { autoLoad: true });

  const {
    vehiculos,       // viene de /api/comparativo/
    vehiculosValidos,
    loading,
    error,
  } = useVehiculos({
    autoRefresh: false,
    intervaloActualizacion: 0,
  });

  useEffect(() => {
    if (vehiculos && vehiculos.length > 0) {
      setHasLoadedOnce(true);
    }
  }, [vehiculos]);

  // Refrescar consumos cuando se dispara el evento de actualizar
  useEffect(() => {
    const handleManualRefresh = () => {
      refrescarConsumos();
    };

    window.addEventListener('refreshTraceabilityData', handleManualRefresh);

    return () => {
      window.removeEventListener('refreshTraceabilityData', handleManualRefresh);
    };
  }, [refrescarConsumos]);

  // Marca → Modelos → Catálogos (estructura base)
  const brandModelData = useMemo(() => {
    if (!vehiculos || vehiculos.length === 0) return [];
    return buildBrandModelCatalogs(vehiculos);
  }, [vehiculos]);

  const isCatalogLoading = loading && !hasLoadedOnce;

  // 🆕 Nuevo manejador que usa la API
  const handleSaveConsumo = useCallback(async (marca, detalle, consumo) => {
    try {
      // Validaciones básicas (ahora las maneja el hook, pero dejamos por seguridad)
      if (!marca || !detalle || !consumo) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Faltan datos requeridos para guardar el consumo',
          duration: 4000
        });
        return;
      }

      if (consumo <= 0) {
        addNotification({
          type: 'error',
          title: 'Valor inválido',
          message: 'El consumo debe ser mayor a 0 km/l',
          duration: 4000
        });
        return;
      }

      // Preparar datos para la API
      const datosConsumo = {
        marca,
        familia: detalle.modelo,
        ano_modelo: detalle.anoModelo,
        modelo_label: detalle.modeloLabel,
        catalogo_codigo: detalle.catalogo,
        consumo_km_l: consumo,
      };

      // Buscar si ya existe
      const consumoExistente = buscarConsumo({
        marca: datosConsumo.marca,
        familia: datosConsumo.familia,
        ano_modelo: datosConsumo.ano_modelo,
        catalogo_codigo: datosConsumo.catalogo_codigo
      });

      // Si existe, mostrar confirmación
      if (consumoExistente) {
        setConfirmDialog({
          isOpen: true,
          title: '¿Actualizar consumo existente?',
          message: `Ya existe un consumo de ${consumoExistente.consumo_km_l} km/l registrado. ¿Deseas actualizarlo a ${consumo} km/l?`,
          type: 'warning',
          isLoading: false,
          onConfirm: async () => {
            try {
              setConfirmDialog(prev => ({ ...prev, isLoading: true, loadingMessage: 'Actualizando consumo...' }));

              // Guardar en la API
              await guardarConsumoAPI(datosConsumo);

              setConfirmDialog({ isOpen: false });

              setOpenBrandId(null);
              setOpenModelBrandId(null);
              setResetTrigger(prev => prev + 1);

              console.log('📦 Payload actualizado:', JSON.stringify(datosConsumo, null, 2));

            } catch (err) {
              setConfirmDialog({ isOpen: false });
              console.error('Error al actualizar:', err);
            }
          }
        });
      } else {
        // Si es nuevo, guardar directamente
        await guardarConsumoAPI(datosConsumo);
        setOpenBrandId(null);
        setOpenModelBrandId(null);
        setResetTrigger(prev => prev + 1);
        console.log('📦 Payload nuevo:', JSON.stringify(datosConsumo, null, 2));
      }

    } catch (err) {
      console.error('Error al procesar consumo:', err);
      // El error ya se maneja en el hook useCombustibles
    }
  }, [addNotification, buscarConsumo, guardarConsumoAPI, setConfirmDialog, setOpenBrandId, setOpenModelBrandId]);

  // Arma TODAS las filas (con y sin consumo) para el modal
  const handleViewConsumos = useCallback(
    async (marca) => {
      const marcaData = brandModelData.find((b) => b.marca === marca);
      if (!marcaData) return;

      let guardados = consumosByMarca.get(marca) || [];

      // Si nunca hemos cargado o no hay nada para esta marca, pega al endpoint
      if (!hasLoadedOnce || guardados.length === 0) {
        try {
          const consumosActualizados = await refrescarConsumos();

          // consumosActualizados viene de la API ya transformados
          if (Array.isArray(consumosActualizados)) {
            guardados = consumosActualizados.filter(
              (c) => c.marca === marca
            );
          }

          if (!hasLoadedOnce) {
            setHasLoadedOnce(true);
          }
        } catch (err) {
          console.error('Error al refrescar consumos para resumen:', err);
          if (addNotification) {
            addNotification({
              type: 'error',
              title: 'Error al cargar consumos',
              message: 'No se pudo obtener el resumen de consumos desde la API',
              duration: 5000,
            });
          }
          return; // no abras el modal si falló
        }
      }

      const rows = [];

      marcaData.modelos.forEach((modelo) => {
        modelo.catalogos.forEach((catalogo) => {
          const found = guardados.find(
            (c) =>
              c.familia === modelo.familia &&
              c.ano_modelo === modelo.anoModelo &&
              c.catalogo_codigo === catalogo.code
          );

          rows.push({
            modeloLabel: modelo.label,      // "BRONCO 2025"
            catalogoLabel: catalogo.label,  // "BR1 - BRONCO"
            familia: modelo.familia,
            ano_modelo: modelo.anoModelo,
            catalogo_codigo: catalogo.code,
            consumo_km_l: found?.consumo_km_l ?? null, // null si no hay registro
          });
        });
      });

      setResumenConsumo({ marca, rows });
      setOpenConsumoMarca(marca);
    },
    [
      brandModelData,
      consumosByMarca,
      hasLoadedOnce,
      refrescarConsumos,
      addNotification,
    ]
  );


  const handleToggleOpen = useCallback((brandId, nextOpen) => {
    setOpenBrandId((prev) => {
      if (!nextOpen) return prev === brandId ? null : prev;
      return brandId;
    });

    if (nextOpen) {
      setOpenModelBrandId(null);
    }
  }, []);

  const handleToggleModelOpen = useCallback((brandId, nextOpen) => {
    setOpenModelBrandId((prev) => {
      if (!nextOpen) return prev === brandId ? null : prev;
      return brandId;
    });

    if (nextOpen) {
      setOpenBrandId(null);
    }
  }, []);

  return (
    <>
      <TraceabilityMainConfig>
        {/* Sección de consumo por catálogo */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            Consumo de combustible
          </h2>
          <p className="text-sm text-gray-600">
            Seleccione una familia y luego el catálogo para capturar el consumo de combustible en km por litro.
          </p>

          {/* Mostrar estado de la API de combustibles */}
          {combustiblesError && (
            <p className="text-sm text-red-500 mt-2">
              Error al conectar con API de combustibles: {combustiblesError}
            </p>
          )}

          {combustiblesLoading && (
            <p className="text-sm text-blue-600 mt-2">
              Cargando datos de consumo...
            </p>
          )}
        </div>

        {/* Cards por marca con modelos y catálogos reales */}
        {isCatalogLoading ? (
          // MIENTRAS CARGA → mostramos un placeholder
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse flex flex-col gap-3"
              >
                <div className="h-4 w-24 bg-gray-200 rounded" />
                <div className="h-3 w-16 bg-gray-200 rounded" />

                <div className="mt-4 space-y-2">
                  <div className="h-3 w-full bg-gray-100 rounded" />
                  <div className="h-3 w-5/6 bg-gray-100 rounded" />
                  <div className="h-3 w-2/3 bg-gray-100 rounded" />
                </div>

                <div className="mt-4 h-8 w-32 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
            {brandModelData.map((brand) => {
              const tieneConsumos = marcaTieneConsumos(brand.marca);

              return (
                <ConsumoCard
                  key={brand.id}
                  marca={brand.marca}
                  modelos={brand.modelos}
                  isOpen={openBrandId === brand.id}
                  onToggleOpen={(nextOpen) => handleToggleOpen(brand.id, nextOpen)}
                  isModelOpen={openModelBrandId === brand.id}
                  onToggleModelOpen={(nextOpen) =>
                    handleToggleModelOpen(brand.id, nextOpen)
                  }
                  onSaveConsumo={handleSaveConsumo}
                  onViewConsumos={() => handleViewConsumos(brand.marca)}
                  hasConsumos={tieneConsumos}
                  isSubmitting={isSubmitting}
                  resetTrigger={resetTrigger}
                />
              );
            })}

            {brandModelData.length === 0 && !loading && (
              <div className="col-span-full text-sm text-gray-500">
                No se encontraron vehículos en el comparativo para construir catálogos.
              </div>
            )}
          </div>
        )}

        {/* Mensaje de error opcional para vehículos */}
        {error && (
          <p className="text-sm text-red-500 mb-2">
            Error al cargar vehículos para distancias: {String(error)}
          </p>
        )}

        {/* Sección de distancias entre sitios */}
        <SiteDistancesSection
          vehiculos={vehiculosValidos || []}
          loading={loading}
          addNotification={addNotification}
          confirmDialog={confirmDialog}
          setConfirmDialog={setConfirmDialog}
        />

        {/* Modal de consumos por marca - Componente mejorado */}
        {openConsumoMarca && (
          <ConsumoResumenModal
            marca={resumenConsumo.marca}
            rows={resumenConsumo.rows}
            onClose={() => setOpenConsumoMarca(null)}
          />
        )}
      </TraceabilityMainConfig>

      {/* Renderizar fuera del DOM de TraceabilityMainConfig */}
      {ReactDOM.createPortal(
        <>
          <NotificationContainer
            notifications={notifications}
            onRemove={removeNotification}
          />
          <ConfirmDialog
            isOpen={confirmDialog.isOpen}
            onClose={() => !confirmDialog.isLoading && setConfirmDialog({ isOpen: false })}
            onConfirm={confirmDialog.onConfirm}
            title={confirmDialog.title}
            message={confirmDialog.message}
            type={confirmDialog.type}
            isLoading={confirmDialog.isLoading}
            loadingMessage={confirmDialog.loadingMessage}
          />
        </>,
        document.body // Renderizar directamente en el body
      )}
    </>
  );
};

export default TraceabilityPageConfig;